import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Clock, CloudOff, Ellipsis, FileDown, FoldHorizontal, ClipboardList, GraduationCap, Link2, ListChecks, ListTree, MessagesSquare, Presentation, UnfoldHorizontal, Lock, LockOpen, MoveRight, Pin, Quote, RotateCcw, Save, Tag, Trash, X, Users, MessageSquare } from "lucide-react";
import { setArchived } from "@/features/archive/ArchiveView";
import { NOTE_KIND, formatTimestamp, parseInternalHref } from "@brainlog/shared";
import { del, errorMessage, get, patch, post, put } from "@/lib/api";
import { qk, useNote } from "@/lib/queries";
import { openView, useUi } from "@/lib/store";
import type { User, Workspace } from "@/lib/types";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TitleInput } from "@/components/ui/title-input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MarkdownEditor, type EditorHandle } from "./MarkdownEditor";
import { MarkdownPreview } from "./MarkdownPreview";
import { NoteOutline } from "./NoteOutline";
import { SlideDeck } from "@/features/slides/SlideDeck";
import { noteHeadings } from "./outline";
import { LinkPanel, type OpenedLink } from "./LinkPanel";
import { useCiteSource } from "@/features/library/useCiteSource";
import { useCommentsInEditor } from "./CommentComposer";
import { LinkOpenerContext, PANEL_LINK_TYPES } from "./linkOpener";
import { useEntryState, useLiveText, usePresence } from "./useCollab";
import { dropPlainCaches, getCollab } from "./collab";
import { getEncryptedCollab, type EncryptedNoteSession } from "@/features/crypto/encryptedCollab";
import { lockScope, useIsUnlocked } from "@/features/crypto/keyring";
import { UnlockPanel, setLiveTitle, useNoteTitle } from "@/features/crypto/UnlockPanel";
import { DecryptDialog, EncryptDialog } from "@/features/crypto/EncryptDialog";
import { ScopeAccessDialog } from "@/features/crypto/ScopeAccess";
import { encTitle, proofFor, saveBlob } from "@/features/crypto/ops";
import { SyncStatus } from "./SyncStatus";
import { KindIcon, kindLabel } from "./kinds";
import { MoveDialog } from "./MoveDialog";
import { VersionsDialog } from "./VersionsDialog";
import { RecordPanel } from "@/features/records/RecordPanel";
import { InsertLinkDialog } from "./InsertLinkDialog";
import { ActionItemsDialog } from "@/features/tasks/ActionItemsDialog";
import { EvidenceLinkDialog } from "@/features/records/EvidenceLinkDialog";
import { setEditorBridge } from "./editorBridge";
import { useMediaQuery } from "@/lib/useMedia";
import { MakeSlidesDialog } from "@/features/slides/MakeSlides";
import { QuestionnaireDialog, StartSessionDialog } from "@/features/learning/StartSession";

export function NoteView({ noteId, user }: { noteId: string; user: User; workspace?: Workspace }) {
  const note = useNote(noteId);
  const qc = useQueryClient();
  const renameTab = useUi((s) => s.renameTab);
  const [moveOpen, setMoveOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState<null | "link" | "cite">(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [selection, setSelection] = useState({ text: "", from: 0, to: 0 });
  const editorRef = useRef<EditorHandle>(null);

  useEffect(() => {
    if (note.data) renameTab(`note:${noteId}`, note.data.note.title);
  }, [note.data?.note.title]); // eslint-disable-line react-hooks/exhaustive-deps

  // A note opened from a link in another notebook/project gets its tab where the note lives.
  useEffect(() => {
    if (note.data) useUi.getState().updateTab(`note:${noteId}`, { workspaceId: note.data.note.workspaceId });
  }, [note.data?.note.workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (note.isLoading) return <p className="p-6 text-muted-foreground" role="status">Loading note…</p>;
  if (note.isError || !note.data)
    return (
      <div className="p-6" role="alert">
        <p className="font-medium">This note is not available.</p>
        <p className="text-sm text-muted-foreground">It may have been deleted, moved to a notebook or project you cannot open, or your access was removed.</p>
      </div>
    );

  const d = note.data;
  const deleted = !!d.note.deletedAt;

  return (
    <NoteGate d={d}>
    <NoteBody
      key={noteId}
      d={d}
      user={user}
      deleted={deleted}
      editorRef={editorRef}
      selection={selection}
      setSelection={setSelection}
      openMove={() => setMoveOpen(true)}
      openVersions={() => setVersionsOpen(true)}
      openLink={(k) => setLinkOpen(k)}
      openActions={() => setActionsOpen(true)}
      openEvidence={() => setEvidenceOpen(true)}
      refresh={() => qc.invalidateQueries({ queryKey: qk.note(noteId) })}
    >
      {moveOpen && <MoveDialog open onOpenChange={setMoveOpen} noteId={noteId} noteTitle={d.note.title} currentWorkspaceId={d.note.workspaceId} />}
      {versionsOpen && <VersionsDialog open onOpenChange={setVersionsOpen} noteId={noteId} />}
      {linkOpen && (
        <InsertLinkDialog
          open
          mode={linkOpen}
          workspaceId={d.note.workspaceId}
          onOpenChange={(o) => !o && setLinkOpen(null)}
          onInsert={(md) => {
            editorRef.current?.insertAtCursor(md);
            setLinkOpen(null);
          }}
        />
      )}
      {actionsOpen && <ActionItemsDialog open onOpenChange={setActionsOpen} noteId={noteId} workspaceId={d.note.workspaceId} />}
      {evidenceOpen && (
        <EvidenceLinkDialog open onOpenChange={setEvidenceOpen} claimNoteId={noteId} workspaceId={d.note.workspaceId} initialClaim={selection.text} />
      )}
    </NoteBody>
    </NoteGate>
  );
}

/** Encrypted notes do not open until their key is unlocked in this tab. */
function NoteGate({ d, children }: { d: NonNullable<ReturnType<typeof useNote>["data"]>; children: React.ReactNode }) {
  const scopeId = d.note.encryptionScopeId;
  const unlocked = useIsUnlocked(scopeId);
  const userId = useUi((s) => s.userId);
  useEffect(() => {
    // Plaintext offline copies from before the note was encrypted must not stay on this device.
    if (scopeId && userId) void dropPlainCaches(userId, [d.note.id]);
  }, [scopeId, userId, d.note.id]);
  if (scopeId && !unlocked) {
    if (!d.encryption) return <p className="p-6 text-sm" role="alert">This note is encrypted, but its key information could not be loaded.</p>;
    return <UnlockPanel scope={d.encryption} />;
  }
  return <>{children}</>;
}

function useNoteEntry(d: NonNullable<ReturnType<typeof useNote>["data"]>, user: User) {
  const scopeId = d.note.encryptionScopeId;
  const epoch = d.note.contentEpoch ?? 0;
  const entry = useMemo(
    () => (scopeId ? getEncryptedCollab(d.note.id, scopeId, user, epoch) : getCollab(d.note.id, user, epoch)),
    [d.note.id, scopeId, epoch, user.id], // eslint-disable-line react-hooks/exhaustive-deps
  );
  return useEntryState(entry);
}

function NoteBody(props: {
  d: NonNullable<ReturnType<typeof useNote>["data"]>;
  user: User;
  deleted: boolean;
  editorRef: React.RefObject<EditorHandle | null>;
  selection: { text: string; from: number; to: number };
  setSelection(s: { text: string; from: number; to: number }): void;
  openMove(): void;
  openVersions(): void;
  openLink(k: "link" | "cite"): void;
  openActions(): void;
  openEvidence(): void;
  refresh(): void;
  children: React.ReactNode;
}) {
  const { d, user, deleted } = props;
  // On phones the side panel covers the note.
  const phone = useMediaQuery("(max-width: 767px)");
  const entry = useNoteEntry(d, user);
  const encrypted = !!d.note.encryptionScopeId;
  const text = useLiveText(entry);
  const presence = usePresence(entry);
  const shownTitle = useNoteTitle(d.note);
  const [title, setTitle] = useState(shownTitle);
  const [tagInput, setTagInput] = useState("");
  const [encryptOpen, setEncryptOpen] = useState<null | "encrypt" | "decrypt" | "access">(null);
  const qc = useQueryClient();
  const archived = !!d.note.archivedAt || !!d.workspace.archivedAt;
  const readOnly = deleted || archived || !!(entry as { frozen?: boolean }).frozen || entry.state === "forbidden" || entry.state === "revoked" || entry.state === "locked";
  const canEncrypt = !encrypted && !deleted && !d.workspace.encryptionScopeId;

  useEffect(() => setTitle(shownTitle), [shownTitle]);
  useEffect(() => {
    if (!encrypted) return;
    (entry as EncryptedNoteSession).title = shownTitle;
    setLiveTitle(d.note.id, shownTitle);
  }, [encrypted, entry, shownTitle, d.note.id]);
  useEffect(() => {
    setEditorBridge(d.note.id, {
      insert: (md: string) => props.editorRef.current?.insertAtCursor(md),
      selection: () => props.editorRef.current?.getSelection() ?? { text: "", from: 0, to: 0 },
      text: () => entry.doc.getText("content").toString(),
    });
    return () => setEditorBridge(d.note.id, null);
  }, [d.note.id, entry]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMeta = async (body: Record<string, unknown>) => {
    try {
      await patch(`/api/notes/${d.note.id}`, { ...body, metaVersion: d.note.metaVersion });
      props.refresh();
      qc.invalidateQueries({ queryKey: qk.tree(d.note.workspaceId) });
    } catch (e) {
      toast.error(errorMessage(e));
      props.refresh();
    }
  };

  const commitTitle = async () => {
    const t = title.trim();
    if (!t) return setTitle(shownTitle);
    if (t === shownTitle) return;
    if (!encrypted) return void saveMeta({ title: t });
    try {
      await put(`/api/notes/${d.note.id}/enc-title`, { encTitle: await encTitle(d.note.encryptionScopeId!, d.note.id, t), keyProof: proofFor(d.note.encryptionScopeId!) });
      props.refresh();
      qc.invalidateQueries({ queryKey: qk.tree(d.note.workspaceId) });
    } catch (e) {
      toast.error(errorMessage(e));
      setTitle(shownTitle);
    }
  };

  const refreshAll = () => {
    props.refresh();
    qc.invalidateQueries({ queryKey: qk.tree(d.note.workspaceId) });
  };

  const addTag = () => {
    const t = tagInput.trim().replace(/^#/, "");
    if (!t) return;
    setTagInput("");
    if (!d.note.tags.includes(t)) void saveMeta({ tags: [...d.note.tags, t] });
  };

  const [panel, setPanel] = useState<OpenedLink[]>([]);
  const cite = useCiteSource(d.note.workspaceId);
  const inEditor = useCommentsInEditor(d.note.id);
  const [addingTag, setAddingTag] = useState(false);
  const openLink = (href: string, context: string) => {
    const link = parseInternalHref(href);
    if (!link) return;
    if (link.type === "manuscript") return openView("manuscript", undefined, "Manuscript", undefined, link.id);
    if (!PANEL_LINK_TYPES.has(link.type)) return;
    setPanel((stack) => (stack.at(-1)?.href === href ? stack : [...stack, { href, context }].slice(-20)));
  };
  const visibility = `${d.workspace.kind === "notebook" ? "Notebook" : "Project"} “${d.workspace.name}” · ${(d.workspace.memberCount ?? 1) > 1 ? "visible to its members" : "private to you"}`;
  const ready = entry.everSynced || entry.fromOfflineCopy || entry.state === "synced" || entry.state === "saved" || entry.state === "saving";
  const noteWidth = useUi((s) => s.noteWidth);
  const showOutline = useUi((s) => s.noteOutline);
  const wide = useMediaQuery("(min-width: 1024px)");
  const full = noteWidth === "full";
  const deferredText = useDeferredValue(ready ? text : d.note.contentMd);
  const headings = useMemo(() => noteHeadings(deferredText), [deferredText]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scroller = useCallback(() => scrollRef.current, []);
  const lineTop = useCallback((line: number) => props.editorRef.current?.lineTop(line) ?? null, [props.editorRef]);
  // The outline needs room: it gives way to the side panel and to narrow screens.
  const outlineShown = showOutline && wide && panel.length === 0 && ready;
  // Presenting the note as slides, in the note's place (the side bars stay unless hidden).
  const [presenting, setPresenting] = useState(false);
  const [makeSlides, setMakeSlides] = useState(false);
  const [sessionDialog, setSessionDialog] = useState<null | "learn" | "grill" | "questionnaire">(null);
  // A note written by a learning session or a plan review links back to it.
  const sessions = useQuery({
    queryKey: ["learning-for-note", d.note.id],
    queryFn: () => get<{ sessions: { id: string; kind: "learn" | "grill"; subject: { title: string } }[] }>(`/api/learning?workspaceId=${d.note.workspaceId}&noteId=${d.note.id}`),
    enabled: !encrypted && !d.workspace.encryptionScopeId,
    staleTime: 60_000,
  });
  const ownSession = sessions.data?.sessions[0];
  const slides = d.note.kind === "slides";

  return (
    <div className="flex h-full min-h-0 flex-col">
      {deleted && (
        <div className="flex items-center gap-3 border-b bg-destructive/10 px-4 py-2 text-sm" role="status">
          <Trash className="size-4" aria-hidden /> This note is in the trash and read-only.
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              await post(`/api/notes/${d.note.id}/restore`);
              props.refresh();
              qc.invalidateQueries({ queryKey: qk.tree(d.note.workspaceId) });
            }}
          >
            <RotateCcw /> Restore
          </Button>
        </div>
      )}
      {!deleted && archived && (
        <div className="flex flex-wrap items-center gap-3 border-b bg-muted px-4 py-2 text-sm" role="status">
          <Archive className="size-4" aria-hidden />
          {d.workspace.archivedAt
            ? `This ${d.workspace.kind === "notebook" ? "notebook" : "project"} is archived: its notes are read-only until it is unarchived (from the Archive, above Settings).`
            : "This note is archived and read-only."}
          {!d.workspace.archivedAt && (
            <Button size="sm" variant="outline" onClick={async () => (await setArchived(qc, { type: "note", id: d.note.id, workspaceId: d.note.workspaceId, name: shownTitle }, false)) && props.refresh()}>
              <ArchiveRestore /> Unarchive
            </Button>
          )}
        </div>
      )}
      {ownSession && !presenting && (
        <div className="flex flex-wrap items-center gap-3 border-b bg-muted/60 px-4 py-1.5 text-sm" role="status">
          {ownSession.kind === "learn" ? <GraduationCap className="size-4" aria-hidden /> : <MessagesSquare className="size-4" aria-hidden />}
          {ownSession.kind === "learn" ? "Lessons from your learning session." : "Questions and decisions from your plan review."}
          <Button size="sm" variant="outline" onClick={() => openView("learning", d.note.workspaceId, `${ownSession.kind === "learn" ? "Learning" : "Plan review"}: ${ownSession.subject.title}`.slice(0, 80), undefined, ownSession.id)}>
            Continue
          </Button>
        </div>
      )}
      {/* A quiet bar: what kind of note this is, its save state and a few tools. */}
      <div className="flex h-10 shrink-0 items-center gap-0.5 px-3 text-xs text-muted-foreground">
        <div className="flex min-w-0 flex-1 items-center gap-3 px-1">
          {d.note.kind !== "note" && (
            <span className="inline-flex items-center gap-1 truncate">
              <KindIcon kind={d.note.kind} className="size-3.5" />
              {kindLabel(d.note.kind)}
            </span>
          )}
          {encrypted && (
            <span className="inline-flex items-center gap-1 text-primary" title="Encrypted in your browser; BrainLog and its AI assistant cannot read it.">
              <Lock className="size-3" aria-hidden /> End-to-end encrypted
            </span>
          )}
          {d.offline && (
            <span className="inline-flex items-center gap-1 text-warning">
              <CloudOff className="size-3" aria-hidden /> Offline copy
            </span>
          )}
        </div>
        <Button
          variant={slides ? "outline" : "ghost"}
          size={slides ? "sm" : "icon-sm"}
          className={cn(slides && "h-7 gap-1 px-2")}
          onClick={() => setPresenting(true)}
          aria-label="Present as slides"
          title={slides ? "Present these slides (in the page or full screen)" : "Present this note as slides: each --- line, or else each title, starts a slide"}
        >
          <Presentation /> {slides && "Present"}
        </Button>
        <SyncStatus entry={entry} />
        {presence.length > 0 && (
          <div className="mx-1 flex -space-x-1.5" aria-label={`Also editing: ${presence.map((p) => p.name).join(", ")}`}>
            {presence.map((p) => (
              <span
                key={p.clientId}
                title={`${p.name} is editing`}
                className="flex size-6 items-center justify-center rounded-full border-2 border-background text-[0.65rem] font-bold text-white"
                style={{ background: p.color }}
              >
                {p.name.slice(0, 1)}
              </span>
            ))}
          </div>
        )}
        {!readOnly && (
          <>
            <Button variant="ghost" size="icon-sm" onClick={() => props.openLink("cite")} aria-label="Insert citation" title="Insert a citation">
              <Quote />
            </Button>
            <Button variant="ghost" size="icon-sm" onClick={() => props.openLink("link")} aria-label="Insert link" title="Insert a link to a note, paper, task or file">
              <Link2 />
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-1.5"
          onClick={() => useUi.getState().setRightPanel("comments")}
          aria-label={`Comments — ${inEditor.openCount} open`}
          title="Comments (select text in the note to comment on it)"
        >
          <MessageSquare />
          {inEditor.openCount > 0 && <span className="text-xs tabular-nums">{inEditor.openCount}</span>}
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          className="hidden lg:inline-flex"
          onClick={() => useUi.getState().setNoteWidth(full ? "readable" : "full")}
          aria-label={full ? "Narrower text" : "Full width"}
          title={full ? "Narrower text, easier to read" : "Use the full width of the page"}
        >
          {full ? <FoldHorizontal /> : <UnfoldHorizontal />}
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          className={cn("hidden lg:inline-flex", showOutline && "text-foreground")}
          onClick={() => useUi.getState().setNoteOutline(!showOutline)}
          aria-label={showOutline ? "Hide the outline" : "Show the outline"}
          aria-pressed={showOutline}
          title={showOutline ? "Hide the outline" : "Show the outline (title and headings)"}
        >
          <ListTree />
        </Button>
        <Button variant="ghost" size="icon-sm" onClick={props.openVersions} aria-label={`History — ${d.versionCount} versions`} title={`Version history (${d.versionCount})`}>
          <Clock />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label="More note actions">
              <Ellipsis />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuItem disabled={readOnly} onSelect={props.openMove}>
              <MoveRight /> Move to…
            </DropdownMenuItem>
            {!encrypted && !readOnly && (
              <DropdownMenuItem onSelect={props.openEvidence}>
                <Link2 className="rotate-45" /> Link the selected text to evidence…
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              disabled={readOnly}
              onSelect={async () => {
                try {
                  const { version } = encrypted
                    ? await (entry as EncryptedNoteSession).saveVersion("manual")
                    : await post<{ version: { versionNo: number } }>(`/api/notes/${d.note.id}/versions`);
                  toast.success(`Saved as version ${version.versionNo}`);
                  props.refresh();
                } catch (e) {
                  toast.error(errorMessage(e));
                }
              }}
            >
              <Save /> Save a version now
            </DropdownMenuItem>
            {!encrypted && (
              <DropdownMenuItem disabled={readOnly} onSelect={props.openActions}>
                <ListChecks /> Create tasks from action items…
              </DropdownMenuItem>
            )}
            {!encrypted && d.note.kind !== "slides" && !d.workspace.encryptionScopeId && (
              <DropdownMenuItem onSelect={() => setMakeSlides(true)}>
                <Presentation /> Make slides from this note…
              </DropdownMenuItem>
            )}
            {!encrypted && !d.workspace.encryptionScopeId && (
              <>
                <DropdownMenuItem onSelect={() => setSessionDialog("learn")}>
                  <GraduationCap /> Learn from this note…
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setSessionDialog("grill")}>
                  <MessagesSquare /> Grill this plan…
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setSessionDialog("questionnaire")}>
                  <ClipboardList /> Questionnaire for a co-author…
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuItem
              onSelect={() =>
                encrypted
                  ? saveBlob(new Blob([`# ${shownTitle}\n\n${entry.doc.getText("content").toString()}`], { type: "text/markdown" }), `${shownTitle.replace(/[\\/:*?"<>|]+/g, "_").slice(0, 80) || "note"}.md`)
                  : window.open(`/api/notes/${d.note.id}/export.md`, "_blank")
              }
            >
              <FileDown /> {encrypted ? "Export decrypted Markdown" : "Export as Markdown"}
            </DropdownMenuItem>
            <DropdownMenuItem disabled={readOnly} onSelect={() => saveMeta({ pinned: !d.note.pinned })}>
              <Pin /> {d.note.pinned ? "Unpin from overview" : "Pin to overview"}
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger disabled={readOnly}>Change record type</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-80 overflow-y-auto">
                {NOTE_KIND.values.map((k) => (
                  <DropdownMenuItem key={k} disabled={k === d.note.kind} onSelect={() => saveMeta({ kind: k })}>
                    <KindIcon kind={k} /> {NOTE_KIND.labels[k]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            {canEncrypt && (
              <DropdownMenuItem onSelect={() => setEncryptOpen("encrypt")}>
                <Lock /> Encrypt note…
              </DropdownMenuItem>
            )}
            {encrypted && !d.workspace.encryptionScopeId && (
              <DropdownMenuItem disabled={deleted} onSelect={() => setEncryptOpen("decrypt")}>
                <LockOpen /> Remove encryption…
              </DropdownMenuItem>
            )}
            {encrypted && d.encryption && (
              <DropdownMenuItem onSelect={() => setEncryptOpen("access")}>
                <Users /> Who can open {d.workspace.encryptionScopeId === d.note.encryptionScopeId ? `this ${d.workspace.kind === "notebook" ? "notebook" : "project"}` : "this note"}…
              </DropdownMenuItem>
            )}
            {encrypted && (
              <DropdownMenuItem onSelect={() => lockScope(d.note.encryptionScopeId!)}>
                <Lock /> Lock now
              </DropdownMenuItem>
            )}
            {!deleted && !d.workspace.archivedAt && d.note.kind !== "manuscript_section" && (
              <DropdownMenuItem
                onSelect={async () => {
                  const archive = !d.note.archivedAt;
                  if (await setArchived(qc, { type: "note", id: d.note.id, workspaceId: d.note.workspaceId, name: shownTitle }, archive)) props.refresh();
                }}
              >
                {d.note.archivedAt ? <ArchiveRestore /> : <Archive />} {d.note.archivedAt ? "Unarchive" : "Archive"}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              disabled={deleted}
              onSelect={async () => {
                await del(`/api/notes/${d.note.id}`);
                props.refresh();
                qc.invalidateQueries({ queryKey: qk.tree(d.note.workspaceId) });
                toast.success("Moved to trash. Restore it from the banner or the Trash view.");
              }}
            >
              <Trash /> Move to trash
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {/* Details that used to sit in the header and footer. */}
            <div className="space-y-0.5 px-2 py-1.5 text-xs text-muted-foreground">
              <p>{visibility}</p>
              {d.manuscriptSections.map((m) => (
                <p key={m.sectionId}>Section of “{m.manuscriptTitle}”</p>
              ))}
              <p>
                Created {formatTimestamp(d.note.createdAt, user.timezone)} · {text.length.toLocaleString()} characters
              </p>
              {encrypted && <p>End-to-end encrypted: BrainLog and its AI assistant cannot read this note.</p>}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {presenting && <SlideDeck markdown={ready ? text : d.note.contentMd} title={title} onClose={() => setPresenting(false)} />}
      {!presenting && d.record && <RecordPanel noteId={d.note.id} kind={d.note.kind} record={d.record} workspaceId={d.note.workspaceId} readOnly={readOnly} onCreateTasks={props.openActions} />}
      <div className={cn("flex min-h-0 flex-1", presenting && "hidden")}>
        <div ref={scrollRef} className={cn("relative min-h-0 min-w-0 flex-1 overflow-y-auto", full && "note-full")} data-note-scroll>
          <div className={cn("mx-auto w-full px-6 pt-5", full ? "max-w-none sm:px-10" : "max-w-[46rem]")}>
            <label htmlFor={`title-${d.note.id}`} className="sr-only">
              Note title
            </label>
            <TitleInput
              id={`title-${d.note.id}`}
              value={title}
              readOnly={deleted || archived}
              placeholder="Untitled"
              onChange={setTitle}
              onCommit={commitTitle}
              onCancel={() => setTitle(shownTitle)}
              className="font-display w-full bg-transparent text-[1.9rem] leading-tight font-semibold outline-none placeholder:text-muted-foreground/60 sm:text-[2.25rem]"
              maxLength={300}
            />
            {(d.note.tags.length > 0 || !(deleted || archived)) && (
              <div className="mt-1.5 flex min-h-6 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                {d.note.tags.map((t) => (
                  <span key={t} className="group inline-flex items-center gap-0.5 rounded-full bg-secondary px-2 py-0.5 text-secondary-foreground">
                    #{t}
                    {!(deleted || archived) && (
                      <button
                        type="button"
                        aria-label={`Remove tag ${t}`}
                        className="rounded-full opacity-0 group-hover:opacity-100 hover:text-foreground focus-visible:opacity-100"
                        onClick={() => saveMeta({ tags: d.note.tags.filter((x) => x !== t) })}
                      >
                        <X className="size-3" />
                      </button>
                    )}
                  </span>
                ))}
                {!(deleted || archived) &&
                  (addingTag ? (
                    <form
                      className="inline-flex items-center"
                      onSubmit={(e) => {
                        e.preventDefault();
                        addTag();
                      }}
                    >
                      <Input
                        autoFocus
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onBlur={() => {
                          addTag();
                          setAddingTag(false);
                        }}
                        onKeyDown={(e) => e.key === "Escape" && setAddingTag(false)}
                        placeholder="tag"
                        aria-label="Add tag"
                        className="h-6 w-28 px-1.5 text-xs"
                        maxLength={60}
                      />
                    </form>
                  ) : (
                    <button type="button" onClick={() => setAddingTag(true)} className="inline-flex items-center gap-0.5 rounded px-1 opacity-70 hover:bg-accent hover:opacity-100">
                      <Tag className="size-3" aria-hidden /> Add tag
                    </button>
                  ))}
              </div>
            )}
          </div>
          {ready ? (
            <MarkdownEditor
              ref={props.editorRef}
              entry={entry}
              readOnly={readOnly}
              onSelectionChange={props.setSelection}
              onOpenLink={openLink}
              cite={cite}
              comments={readOnly ? undefined : inEditor.comments}
              ariaLabel={`${d.note.title} — note text`}
            />
          ) : entry.state === "forbidden" || entry.state === "revoked" ? (
            <p className={cn("mx-auto px-6 py-4 text-sm", full ? "max-w-none sm:px-10" : "max-w-[46rem]")} role="alert">
              {entry.error}
            </p>
          ) : (
            <div className={cn("mx-auto px-6 py-4 text-sm text-muted-foreground", full ? "max-w-none sm:px-10" : "max-w-[46rem]")} role="status">
              {encrypted ? "Loading and decrypting the note…" : "Connecting… The last saved text is shown until the connection is ready."}
              {!encrypted && (
                <LinkOpenerContext.Provider value={openLink}>
                  <MarkdownPreview markdown={d.note.contentMd} className="mt-4 opacity-80" />
                </LinkOpenerContext.Provider>
              )}
            </div>
          )}
        </div>
        {outlineShown && (
          <aside className="w-60 shrink-0 border-l bg-sidebar/20 xl:w-64" aria-label="Outline">
            <NoteOutline title={title} headings={headings} scroller={scroller} lineTop={lineTop} onClose={() => useUi.getState().setNoteOutline(false)} />
          </aside>
        )}
        {panel.length > 0 && (
          <aside className={cn(phone ? "fixed inset-0 z-40 bg-background" : "w-[min(32rem,45%)] shrink-0 border-l bg-sidebar/30")} aria-label="Beside the note">
            <LinkPanel stack={panel} onBack={() => setPanel((s) => s.slice(0, -1))} onClose={() => setPanel([])} onOpen={openLink} />
          </aside>
        )}
      </div>
      {encryptOpen === "encrypt" && (
        <EncryptDialog open onOpenChange={(o) => !o && setEncryptOpen(null)} target={{ type: "note", id: d.note.id, title: shownTitle, workspaceId: d.note.workspaceId }} onDone={refreshAll} />
      )}
      {encryptOpen === "access" && d.encryption && (
        <ScopeAccessDialog
          open
          onOpenChange={(o) => !o && setEncryptOpen(null)}
          scope={d.encryption}
          what={d.workspace.encryptionScopeId === d.note.encryptionScopeId ? (d.workspace.kind === "notebook" ? "notebook" : "project") : "note"}
        />
      )}
      {encryptOpen === "decrypt" && (
        <DecryptDialog open onOpenChange={(o) => !o && setEncryptOpen(null)} target={{ type: "note", id: d.note.id, title: shownTitle }} onDone={refreshAll} />
      )}
      {sessionDialog === "questionnaire" && <QuestionnaireDialog workspaceId={d.note.workspaceId} noteId={d.note.id} onClose={() => setSessionDialog(null)} />}
      {(sessionDialog === "learn" || sessionDialog === "grill") && (
        <StartSessionDialog workspaceId={d.note.workspaceId} kind={sessionDialog} scope="note" id={d.note.id} title={shownTitle} onClose={() => setSessionDialog(null)} />
      )}
      {makeSlides && <MakeSlidesDialog workspaceId={d.note.workspaceId} scope="note" id={d.note.id} onClose={() => setMakeSlides(false)} />}
      {inEditor.composer}
      {props.children}
    </div>
  );
}
